import React, { useState, useEffect } from 'react';
import BreakCrumbs from '../../components/BreakCrumbs';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import { DatePicker } from '@material-ui/pickers';
import TableLoading from '../../components/TableLoading';
import { alertError } from '../../Util/Util';
import TableEmpty from '../../components/TableEmpty';
import {
  LIST_PAGE_COUNT,
  UserProductPackageOrderStatusText,
} from '../../data/common';
import productPackageAPI from '../../middleware/productPackageAPI';
import { map } from 'lodash';
import UserProductPackageOrderTable from '../../components/ProductPackages/UserProductPackageOrderTable';
import UserProductPackageOrderDetail from '../../components/ProductPackages/UserProductPackageOrderDetail';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const UserProductPackageOrderList = (props) => {
  const { match } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [userProductPackageOrders, setUserProductPackageOrders] = useState();
  const [totalCount, setTotalCount] = useState();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState({
    status: -1,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [userId, setUserId] = useState('');
  const [detail, setDetail] = useState();
  const [isOpenDetail, setIsOpenDetail] = useState(false);

  // 下載報表相關狀態
  const [isOpenDownloadModal, setIsOpenDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadDateRange, setDownloadDateRange] = useState(() => {
    const lastMonth = subMonths(new Date(), 1);
    return {
      startAt: startOfMonth(lastMonth),
      endAt: endOfMonth(lastMonth),
    };
  });
  const [dateRangeError, setDateRangeError] = useState('');

  useEffect(() => {
    getUserProductPackageOrders();
  }, [page]);

  useEffect(() => {
    if (detail && userProductPackageOrders) {
      const updatedDetail = userProductPackageOrders.find(
        (order) => order.orderNumber === detail.orderNumber,
      );

      if (updatedDetail) {
        setDetail(updatedDetail);
      }
    }
  }, [userProductPackageOrders]);

  const getUserProductPackageOrders = () => {
    setIsLoading(true);

    const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : '';
    const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : '';

    productPackageAPI
      .getUserProductPackages(
        page,
        LIST_PAGE_COUNT,
        search.status,
        startDateStr,
        endDateStr,
        keyword,
        userId,
      )
      .then(
        (rs) => {
          setTotalCount(rs.totalCount);
          setUserProductPackageOrders(rs.userProductPackageList);
        },
        (error) => {
          alertError(error);
        },
      )
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleChangeSearch = (name, value) => {
    setSearch({
      ...search,
      [name]: value,
    });
  };

  const handleSearch = () => {
    if (page === 1) {
      getUserProductPackageOrders();
    } else {
      setPage(1);
    }
  };

  const openDetail = (userProductPackageOrder) => {
    setDetail(userProductPackageOrder);
    setIsOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setDetail(undefined);
    setIsOpenDetail(false);
  };

  const handleOpenDownloadModal = () => {
    const lastMonth = subMonths(new Date(), 1);
    setDownloadDateRange({
      startAt: startOfMonth(lastMonth),
      endAt: endOfMonth(lastMonth),
    });
    setDateRangeError('');
    setIsOpenDownloadModal(true);
  };

  const handleCloseDownloadModal = () => {
    setIsOpenDownloadModal(false);
  };

  const handleChangeDateRange = (name, value) => {
    const newDateRange = {
      ...downloadDateRange,
      [name]: value,
    };
    setDownloadDateRange(newDateRange);

    // 驗證日期範圍
    if (newDateRange.startAt && newDateRange.endAt) {
      if (newDateRange.endAt < newDateRange.startAt) {
        setDateRangeError('結束日期必須在開始日期之後');
      } else {
        setDateRangeError('');
      }
    } else {
      setDateRangeError('');
    }
  };

  const isDateRangeValid = () => {
    if (!downloadDateRange.startAt || !downloadDateRange.endAt) {
      return false;
    }
    if (downloadDateRange.endAt < downloadDateRange.startAt) {
      return false;
    }
    return true;
  };

  const handleDownloadExcel = () => {
    if (!isDateRangeValid()) {
      return;
    }

    setIsDownloading(true);

    const startAtStr = format(downloadDateRange.startAt, 'yyyy-MM-dd');
    const endAtStr = format(downloadDateRange.endAt, 'yyyy-MM-dd');

    productPackageAPI
      .downloadProductPackageUsageExcel(startAtStr, endAtStr)
      .then(
        (response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `套券使用情況_${startAtStr}_${endAtStr}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          handleCloseDownloadModal();
        },
        (error) => {
          alertError(error);
        },
      )
      .then(() => {
        setIsDownloading(false);
      });
  };

  return (
    <div className="popular pagefadeIn">
      <BreakCrumbs path={match.url}></BreakCrumbs>

      <Box component={Paper} square elevation={2} p={1} mt={3} mb={3}>
        <Grid
          container
          direction="row"
          justify="space-around"
          alignItems="center">
          <Grid item xs>
            <Typography variant="h4" color="textPrimary" gutterBottom>
              銷售套券記錄
            </Typography>
          </Grid>
        </Grid>

        <Divider light />

        <Box textAlign="right" mt={2} mb={2}>
          <DatePicker
            autoOk
            label="開始時間"
            clearable
            disableFuture
            inputVariant="outlined"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            format="yyyy-MM-dd"
            size="small"
            disabled={isLoading}
            style={{ margin: '6px 12px 6px 0', width: '150px' }}
          />
          <DatePicker
            autoOk
            label="結束時間"
            clearable
            disableFuture
            inputVariant="outlined"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            format="yyyy-MM-dd"
            size="small"
            disabled={isLoading}
            style={{ margin: '6px 12px 6px 0', width: '150px' }}
          />
          <TextField
            label="姓名/手機/Email"
            type="search"
            variant="outlined"
            size="small"
            disabled={isLoading}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            style={{ margin: '6px 12px 6px 0', width: '200px' }}
          />
          <TextField
            label="User ID"
            type="search"
            variant="outlined"
            size="small"
            disabled={isLoading}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            style={{ margin: '6px 12px 6px 0', width: '120px' }}
          />
          <FormControl
            size="small"
            variant="outlined"
            style={{ marginRight: '12px', minWidth: '150px' }}
            disabled={isLoading}>
            <InputLabel>狀態</InputLabel>
            <Select
              value={search.status}
              name="status"
              onChange={(e) => {
                handleChangeSearch('status', e.target.value);
              }}
              label="狀態">
              <MenuItem value="-1">不設定</MenuItem>
              {map(UserProductPackageOrderStatusText, (text, status) => {
                return (
                  <MenuItem value={status} key={status}>
                    {text}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            disabled={isLoading}
            onClick={handleSearch}
            style={{ marginRight: '12px' }}>
            搜尋
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<GetAppIcon />}
            disabled={isLoading}
            onClick={handleOpenDownloadModal}>
            下載套券報表
          </Button>
        </Box>

        {isLoading && <TableLoading />}

        {!isLoading &&
          (!userProductPackageOrders ||
            userProductPackageOrders.length <= 0) && (
            <TableEmpty message="查無銷售套券記錄" />
          )}

        {!isLoading &&
          userProductPackageOrders &&
          userProductPackageOrders.length > 0 && (
            <UserProductPackageOrderTable
              userProductPackageOrders={userProductPackageOrders}
              totalCount={totalCount}
              openDetail={openDetail}
              page={page}
              setPage={setPage}
            />
          )}
      </Box>

      <Drawer anchor="right" open={isOpenDetail} onClose={handleCloseDetail}>
        <UserProductPackageOrderDetail
          userProductPackageOrder={detail}
          closeDrawer={handleCloseDetail}
          onReload={getUserProductPackageOrders}
        />
      </Drawer>

      <Dialog
        open={isOpenDownloadModal}
        onClose={handleCloseDownloadModal}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>下載套券使用情況報表</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <DatePicker
              fullWidth
              label="開始日期"
              value={downloadDateRange.startAt}
              onChange={(date) => handleChangeDateRange('startAt', date)}
              format="yyyy-MM-dd"
              inputVariant="outlined"
              margin="normal"
              disabled={isDownloading}
              disableFuture
              autoOk
            />
            <DatePicker
              fullWidth
              label="結束日期"
              value={downloadDateRange.endAt}
              onChange={(date) => handleChangeDateRange('endAt', date)}
              format="yyyy-MM-dd"
              inputVariant="outlined"
              margin="normal"
              disabled={isDownloading}
              disableFuture
              autoOk
            />
            {dateRangeError && (
              <Typography
                variant="body2"
                color="error"
                style={{ marginTop: '8px' }}>
                {dateRangeError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownloadModal} disabled={isDownloading}>
            取消
          </Button>
          <Button
            onClick={handleDownloadExcel}
            color="primary"
            variant="contained"
            startIcon={<GetAppIcon />}
            disabled={isDownloading || !isDateRangeValid()}>
            {isDownloading ? '下載中...' : '下載'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserProductPackageOrderList;
